/* ============================================================
   AIWA Sentient Search — brain
   - Custom element <aiwa-sentient-search>
   - No body scroll-lock (page scroll stays active while open)
   - Typeahead via /search/suggest.json (Shopify native)
   - Intent-predicted chips, recency pinning, zero-result rescue
   - Behavioral glow (scroll/dwell/exit-intent)
   - dataLayer + Supabase analytics
   ============================================================ */

(function () {
  'use strict';

  // ------------------------------------------------------------
  // Constants
  // ------------------------------------------------------------
  const SUPABASE_LOG_URL = 'https://uxdxvxlqfjnjwjczsbby.supabase.co/functions/v1/log_search_event';
  const DEBOUNCE_MS = 140;
  const RECENT_TTL_MS = 14 * 24 * 60 * 60 * 1000;   // 14 days
  const GLOW_DISMISS_TTL_MS = 24 * 60 * 60 * 1000;  // 24 hours
  const STORAGE_KEYS = {
    recentSearches: 'aiwa_recent_searches',
    recentViews: 'aiwa_recent_views',
    glowDismissedAt: 'aiwa_search_glow_dismissed_at',
    sessionId: 'aiwa_search_session_id',
    icp: 'aiwa_icp_inferred'
  };

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));

  function nowMs() { return Date.now(); }

  function getOrCreateSessionId() {
    let id = sessionStorage.getItem(STORAGE_KEYS.sessionId);
    if (!id) {
      id = 'ss_' + Math.random().toString(36).slice(2, 10) + '_' + nowMs().toString(36);
      sessionStorage.setItem(STORAGE_KEYS.sessionId, id);
    }
    return id;
  }

  function safeJSON(val, fallback) {
    if (!val) return fallback;
    try { return JSON.parse(val); } catch { return fallback; }
  }

  function readTTLList(key, ttl) {
    const list = safeJSON(localStorage.getItem(key), []);
    const cutoff = nowMs() - ttl;
    return list.filter(it => (it.ts || 0) > cutoff);
  }

  function writeTTLList(key, list, cap = 5) {
    try { localStorage.setItem(key, JSON.stringify(list.slice(0, cap))); } catch {}
  }

  function pushRecent(key, value, cap = 5) {
    const list = readTTLList(key, RECENT_TTL_MS);
    const existing = list.findIndex(it => it.v === value);
    if (existing >= 0) list.splice(existing, 1);
    list.unshift({ v: value, ts: nowMs() });
    writeTTLList(key, list, cap);
  }

  function removeRecent(key, value) {
    const list = readTTLList(key, RECENT_TTL_MS).filter(it => it.v !== value);
    writeTTLList(key, list);
  }

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // Shopify predictive search returns `price` as a string "145.00" (decimal),
  // not cents. Handle both shapes defensively.
  function formatPrice(val, currency) {
    if (val == null || val === '') return '';
    let n = typeof val === 'string' ? parseFloat(val) : val;
    if (!Number.isFinite(n)) return '';
    const sym = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AED';
    return `${sym} ${n.toFixed(0)}`;
  }

  // Debounce
  function debounce(fn, ms) {
    let t;
    return function () {
      const args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }

  // Arabic normalization
  function normalizeArabic(s) {
    return String(s || '')
      .replace(/[\u0623\u0625\u0622]/g, '\u0627')   // أإآ -> ا
      .replace(/\u0649/g, '\u064A')                  // ى -> ي
      .replace(/\u0629/g, '\u0647')                  // ة -> ه
      .replace(/[\u064B-\u065F]/g, '');              // diacritics
  }

  function normalizeQuery(q, intents) {
    const lower = (q || '').trim().toLowerCase();
    if (!lower) return '';
    // Arabic → English first (if present)
    if (intents && intents.arabic_to_english) {
      const normArabic = normalizeArabic(lower);
      for (const [ar, en] of Object.entries(intents.arabic_to_english)) {
        if (normArabic.includes(normalizeArabic(ar))) {
          return en;
        }
      }
    }
    // Synonym mapping
    if (intents && intents.synonyms && intents.synonyms[lower]) {
      return intents.synonyms[lower];
    }
    return lower;
  }

  // ------------------------------------------------------------
  // Analytics
  // ------------------------------------------------------------
  function logEvent(payload) {
    // 1) GTM dataLayer
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'aiwa_search_' + payload.event_type }, payload));
    } catch {}

    // 2) Supabase Edge Fn (fire-and-forget, Beacon API preferred).
    // Use text/plain to avoid CORS preflight — the edge fn parses JSON manually.
    try {
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        navigator.sendBeacon(SUPABASE_LOG_URL, blob);
      } else {
        fetch(SUPABASE_LOG_URL, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          keepalive: true
        }).catch(() => {});
      }
    } catch {}
  }

  // ------------------------------------------------------------
  // Track recent product views (runs on every pageview)
  // ------------------------------------------------------------
  (function trackRecentView() {
    const path = location.pathname;
    const m = path.match(/^\/products\/([^\/?#]+)/);
    if (!m) return;
    const handle = m[1];
    const title = document.title.replace(/\s*[|–—-]\s*AIWA.*$/i, '').trim();
    pushRecent(STORAGE_KEYS.recentViews, JSON.stringify({ handle, title, path }), 5);
  })();

  // ------------------------------------------------------------
  // Infer ICP from referrer on first pageview of session
  // ------------------------------------------------------------
  function inferICP(intents) {
    let icp = sessionStorage.getItem(STORAGE_KEYS.icp);
    if (icp) return icp;

    const ref = document.referrer || '';
    const host = (() => { try { return new URL(ref).host; } catch { return ''; } })();

    icp = 'default';
    if (host && intents && intents.referrer_icp_map) {
      for (const [domain, mappedIcp] of Object.entries(intents.referrer_icp_map)) {
        if (host.includes(domain)) { icp = mappedIcp; break; }
      }
    }

    // Time-of-day override for cross_border / late-night urgency
    const hour = new Date().getHours();
    if (hour >= 22 || hour < 6) {
      // Late night → emphasize same-day urgency
      sessionStorage.setItem('aiwa_time_bucket', 'late_night');
    } else if (hour < 12) {
      sessionStorage.setItem('aiwa_time_bucket', 'morning');
    } else if (hour < 17) {
      sessionStorage.setItem('aiwa_time_bucket', 'afternoon');
    } else {
      sessionStorage.setItem('aiwa_time_bucket', 'evening');
    }

    sessionStorage.setItem(STORAGE_KEYS.icp, icp);
    return icp;
  }

  // ------------------------------------------------------------
  // Current-page context → chip seed
  // ------------------------------------------------------------
  function getPageContext() {
    const path = location.pathname;
    const ctx = { type: 'home' };
    let m;
    if ((m = path.match(/^\/collections\/([^\/?#]+)/))) {
      ctx.type = 'collection';
      ctx.handle = m[1];
    } else if ((m = path.match(/^\/products\/([^\/?#]+)/))) {
      ctx.type = 'product';
      ctx.handle = m[1];
    } else if (/^\/pages\//.test(path)) ctx.type = 'page';
    else if (path === '/search') ctx.type = 'search';
    return ctx;
  }

  // ------------------------------------------------------------
  // The element
  // ------------------------------------------------------------
  class AiwaSentientSearch extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.intents = null;
      this.lastQuery = '';
      this.lastQueryAt = 0;
      this.selectedIndex = -1;
      this.currentResults = [];
      this.sessionId = getOrCreateSessionId();
      this.abortCtrl = null;
      this._glowDispatched = false;
      this._scrollEvents = [];      // timestamps of scroll direction changes
      this._lastScrollY = 0;
      this._lastScrollDir = 0;
      this._dwellTimer = null;
      this._openedAt = 0;
    }

    async connectedCallback() {
      this.trigger = $(this, '[data-aiwa-ss-trigger]');
      this.panel = $(this, '[data-aiwa-ss-panel]');

      // Relocate the panel to <body> so `position:fixed` escapes the header's
      // sticky stacking context (z-index collisions on desktop).
      if (this.panel && this.panel.parentElement !== document.body) {
        document.body.appendChild(this.panel);
      }

      this.backdrop = this.panel.querySelector('[data-aiwa-ss-backdrop]');
      this.sheet = this.panel.querySelector('[data-aiwa-ss-sheet]');
      this.form = this.panel.querySelector('[data-aiwa-ss-form]');
      this.input = this.panel.querySelector('[data-aiwa-ss-input]');
      this.clearBtn = this.panel.querySelector('[data-aiwa-ss-clear]');
      this.closeBtn = this.panel.querySelector('[data-aiwa-ss-close]');
      this.emptyEl = this.panel.querySelector('[data-aiwa-ss-empty]');
      this.resultsEl = this.panel.querySelector('[data-aiwa-ss-results]');
      this.zeroEl = this.panel.querySelector('[data-aiwa-ss-zero]');

      this.locale = this.dataset.locale || 'en';
      this.isRTL = this.dataset.dir === 'rtl';
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.predictiveUrl = this.dataset.predictiveUrl || '/search/suggest.json';
      this.intentsUrl = this.dataset.intentsUrl;

      this.customerId = this.dataset.customerId || null;
      this.customerLastOrder = this.dataset.customerLastOrderHandle
        ? { handle: this.dataset.customerLastOrderHandle, title: this.dataset.customerLastOrderTitle || '' }
        : null;

      // Load intents
      try {
        const res = await fetch(this.intentsUrl, { cache: 'force-cache' });
        this.intents = await res.json();
      } catch (e) {
        this.intents = { synonyms: {}, arabic_to_english: {}, icp_chips: { default: [] }, placeholders: { en: ['Search…'], ar: ['ابحث…'] } };
      }

      // Infer ICP now that intents are loaded
      this.icp = inferICP(this.intents);

      // Wire events
      this.trigger.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
      this.backdrop.addEventListener('click', () => this.close());
      this.closeBtn.addEventListener('click', () => this.close());
      this.clearBtn.addEventListener('click', () => this.clearInput());
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.input.addEventListener('input', debounce(() => this.onInput(), DEBOUNCE_MS));
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));

      // ESC closes (global)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) { this.close(); }
        // ⌘K / Ctrl+K opens
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !this.isOpen) {
          e.preventDefault();
          this.open();
        }
      });

      // Swipe down to dismiss (mobile)
      this.setupSwipeToClose();

      // Behavioral signals
      this.setupBehavioralGlow();

      // Set sheet anchor top to header bottom on desktop
      this.updateHeaderOffset();
      window.addEventListener('resize', () => this.updateHeaderOffset(), { passive: true });
      window.addEventListener('scroll', () => this.updateHeaderOffset(), { passive: true });
    }

    updateHeaderOffset() {
      const header = document.querySelector('.section-header, header.header, .header-wrapper');
      if (!header) return;
      const rect = header.getBoundingClientRect();
      const bottom = Math.max(0, rect.bottom);
      document.documentElement.style.setProperty('--aiwa-ss-header-height', `${bottom}px`);
      if (this.sheet && window.innerWidth < 750) {
        this.sheet.style.insetBlockStart = `${bottom}px`;
      } else if (this.sheet) {
        this.sheet.style.insetBlockStart = '';
      }
    }

    // --------------------------------------------------------
    // Open / close
    // --------------------------------------------------------
    toggle() { this.isOpen ? this.close() : this.open(); }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this._openedAt = nowMs();
      this.panel.hidden = false;
      // Force reflow so transition applies
      requestAnimationFrame(() => { this.panel.setAttribute('data-open', 'true'); });
      this.trigger.setAttribute('aria-expanded', 'true');
      this.trigger.removeAttribute('data-glow');
      this.updateHeaderOffset();

      // Render empty state (chips / recent / personalized)
      this.renderEmpty();

      // Focus input after transition
      setTimeout(() => { try { this.input.focus({ preventScroll: true }); } catch {} }, 60);

      logEvent({
        session_id: this.sessionId,
        event_type: 'open',
        locale: this.locale,
        referrer: document.referrer || '',
        user_agent: navigator.userAgent
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        this.panel.hidden = true;
      }, 260);
      try { this.trigger.focus({ preventScroll: true }); } catch {}

      logEvent({
        session_id: this.sessionId,
        event_type: 'dismiss',
        locale: this.locale,
        ms_to_click: this._openedAt ? nowMs() - this._openedAt : null
      });
    }

    clearInput() {
      this.input.value = '';
      this.clearBtn.hidden = true;
      this.renderEmpty();
      this.input.focus();
    }

    // --------------------------------------------------------
    // Swipe-down to close (mobile)
    // --------------------------------------------------------
    setupSwipeToClose() {
      let startY = 0, lastY = 0, tracking = false;

      const onStart = (e) => {
        if (!this.isOpen) return;
        if (window.innerWidth >= 750) return;
        // Only start if user touches the top ~48px of sheet
        const t = e.touches ? e.touches[0] : e;
        const sheetRect = this.sheet.getBoundingClientRect();
        if (t.clientY > sheetRect.top + 60) return;
        startY = t.clientY; lastY = startY; tracking = true;
        this.sheet.style.transition = 'none';
      };
      const onMove = (e) => {
        if (!tracking) return;
        const t = e.touches ? e.touches[0] : e;
        lastY = t.clientY;
        const dy = Math.max(0, lastY - startY);
        this.sheet.style.transform = `translateY(${dy * 0.6}px)`;
        this.sheet.style.opacity = String(Math.max(0.5, 1 - dy / 320));
      };
      const onEnd = () => {
        if (!tracking) return;
        const dy = lastY - startY;
        this.sheet.style.transition = '';
        this.sheet.style.transform = '';
        this.sheet.style.opacity = '';
        if (dy > 80) this.close();
        tracking = false;
      };

      this.sheet.addEventListener('touchstart', onStart, { passive: true });
      this.sheet.addEventListener('touchmove', onMove, { passive: true });
      this.sheet.addEventListener('touchend', onEnd);
      this.sheet.addEventListener('touchcancel', onEnd);
    }

    // --------------------------------------------------------
    // Behavioral glow
    // --------------------------------------------------------
    setupBehavioralGlow() {
      const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEYS.glowDismissedAt) || '0', 10);
      if (nowMs() - dismissedAt < GLOW_DISMISS_TTL_MS) return;

      this._lastScrollY = window.scrollY;

      const onScroll = () => {
        const y = window.scrollY;
        const dir = y > this._lastScrollY ? 1 : y < this._lastScrollY ? -1 : 0;
        if (dir !== 0 && dir !== this._lastScrollDir) {
          this._scrollEvents.push(nowMs());
          // Cap
          if (this._scrollEvents.length > 10) this._scrollEvents.shift();
          // Check: 3+ direction changes in 5s
          const fiveSecAgo = nowMs() - 5000;
          const recent = this._scrollEvents.filter(t => t > fiveSecAgo);
          if (recent.length >= 3 && !this.isOpen) this.triggerGlow('rapid_scroll');
        }
        this._lastScrollY = y;
        if (dir !== 0) this._lastScrollDir = dir;

        // Dwell past 40% → 8s timer
        const pct = (y + window.innerHeight) / document.documentElement.scrollHeight;
        if (pct > 0.4 && !this._dwellTimer && !this.isOpen) {
          this._dwellTimer = setTimeout(() => {
            if (!this.isOpen) this.triggerGlow('dwell');
            this._dwellTimer = null;
          }, 8000);
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });

      // Desktop exit intent
      if (window.innerWidth >= 750) {
        document.addEventListener('mouseleave', (e) => {
          if (e.clientY < 10 && !this.isOpen) this.triggerGlow('exit_intent');
        });
      }
    }

    triggerGlow(reason) {
      if (this._glowDispatched) return;
      this._glowDispatched = true;
      this.trigger.setAttribute('data-glow', 'on');
      setTimeout(() => {
        this.trigger.removeAttribute('data-glow');
        this._glowDispatched = false;
      }, 3800);

      logEvent({ session_id: this.sessionId, event_type: 'glow_trigger', query: reason, locale: this.locale });

      // User can dismiss glow-forever by closing the panel once
      localStorage.setItem(STORAGE_KEYS.glowDismissedAt, String(nowMs()));
    }

    // --------------------------------------------------------
    // Input handling
    // --------------------------------------------------------
    onInput() {
      const raw = this.input.value.trim();
      this.clearBtn.hidden = raw.length === 0;
      if (raw.length === 0) {
        this.renderEmpty();
        return;
      }
      if (raw.length < 2) return;  // require 2+ chars
      this.query(raw);
    }

    onSubmit(e) {
      e.preventDefault();
      const q = this.input.value.trim();
      if (q.length === 0) return;
      const normalized = normalizeQuery(q, this.intents);
      pushRecent(STORAGE_KEYS.recentSearches, q);
      logEvent({
        session_id: this.sessionId,
        event_type: 'click',
        query: q,
        clicked_result: 'submit_to_search_page',
        ms_to_click: this._openedAt ? nowMs() - this._openedAt : null,
        locale: this.locale
      });
      window.location.href = `${this.searchUrl}?q=${encodeURIComponent(normalized)}&options%5Bprefix%5D=last`;
    }

    onKeydown(e) {
      if (!['ArrowDown','ArrowUp','Enter'].includes(e.key)) return;
      const items = $$(this.resultsEl, '[data-aiwa-ss-result]');
      if (items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedIndex = Math.min(items.length - 1, this.selectedIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedIndex = Math.max(0, this.selectedIndex - 1);
      } else if (e.key === 'Enter') {
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          e.preventDefault();
          const url = items[this.selectedIndex].getAttribute('href');
          if (url) {
            this.logClick(items[this.selectedIndex], 'enter_key');
            window.location.href = url;
          }
          return;
        }
      }
      items.forEach((it, i) => it.setAttribute('aria-selected', i === this.selectedIndex ? 'true' : 'false'));
      if (items[this.selectedIndex]) items[this.selectedIndex].scrollIntoView({ block: 'nearest' });
    }

    // --------------------------------------------------------
    // Query → Shopify predictive search
    // --------------------------------------------------------
    async query(raw) {
      const normalized = normalizeQuery(raw, this.intents);
      this.lastQuery = raw;
      this.lastNormalizedQuery = normalized;
      this.lastQueryAt = nowMs();

      // Cancel in-flight
      if (this.abortCtrl) this.abortCtrl.abort();
      this.abortCtrl = new AbortController();

      // Show skeleton
      this.emptyEl.hidden = true;
      this.zeroEl.hidden = true;
      this.resultsEl.hidden = false;
      this.resultsEl.innerHTML = '<div class="aiwa-ss__loading">'
        + '<div class="aiwa-ss__skeleton"></div>'.repeat(3)
        + '</div>';

      const url = `${this.predictiveUrl}?q=${encodeURIComponent(normalized)}`
        + `&resources[type]=product,collection,article,page`
        + `&resources[limit]=6`
        + `&section_id=predictive-search`;

      // Use Shopify JSON endpoint instead
      const jsonUrl = `/search/suggest.json?q=${encodeURIComponent(normalized)}`
        + `&resources[type]=product,collection,article,page&resources[limit]=6`;

      try {
        const res = await fetch(jsonUrl, { signal: this.abortCtrl.signal, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Fetch failed');
        const data = await res.json();
        this.renderResults(raw, data.resources && data.resources.results);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this.renderZero(raw);
      }
    }

    renderResults(rawQuery, results) {
      const products    = (results && results.products)    || [];
      const collections = (results && results.collections) || [];
      const articles    = (results && results.articles)    || [];
      const pages       = (results && results.pages)       || [];

      const total = products.length + collections.length + articles.length + pages.length;

      logEvent({
        session_id: this.sessionId,
        event_type: 'query',
        query: rawQuery,
        result_count: total,
        locale: this.locale
      });

      if (total === 0) { this.renderZero(rawQuery); return; }

      let html = '';

      if (products.length) {
        html += '<div class="aiwa-ss__results-group">'
          + `<div class="aiwa-ss__section-title">${this.t('Products','منتجات')}</div>`
          + '<div class="aiwa-ss__products">'
          + products.map(p => this.productTileHTML(p)).join('')
          + '</div></div>';
      }
      if (collections.length) {
        html += '<div class="aiwa-ss__results-group">'
          + `<div class="aiwa-ss__section-title">${this.t('Collections','مجموعات')}</div>`
          + collections.map(c => this.collectionHTML(c)).join('')
          + '</div>';
      }
      if (articles.length) {
        html += '<div class="aiwa-ss__results-group">'
          + `<div class="aiwa-ss__section-title">${this.t('Articles','مقالات')}</div>`
          + articles.map(a => this.articleHTML(a)).join('')
          + '</div>';
      }
      if (pages.length) {
        html += '<div class="aiwa-ss__results-group">'
          + `<div class="aiwa-ss__section-title">${this.t('Pages','صفحات')}</div>`
          + pages.map(p => this.pageHTML(p)).join('')
          + '</div>';
      }

      // "See all N results" row
      html += `<a class="aiwa-ss__chip aiwa-ss__chip--pinned" style="margin-top:14px;display:inline-flex;"
                  href="${this.searchUrl}?q=${encodeURIComponent(this.lastNormalizedQuery || rawQuery)}&options%5Bprefix%5D=last"
                  data-aiwa-ss-result data-kind="see_all">
                  ${this.t(`See all results for "${escHtml(rawQuery)}"`,`كل النتائج لـ "${escHtml(rawQuery)}"`)}
               </a>`;

      this.emptyEl.hidden = true;
      this.zeroEl.hidden = true;
      this.resultsEl.hidden = false;
      this.resultsEl.innerHTML = html;
      this.selectedIndex = -1;

      // Wire result clicks for logging
      $$(this.resultsEl, '[data-aiwa-ss-result]').forEach(el => {
        el.addEventListener('click', () => this.logClick(el, 'mouse_click'));
      });
    }

    productTileHTML(p) {
      const img = (p.featured_image && p.featured_image.url)
                ? p.featured_image.url + '&width=120'
                : (p.image || '');
      const imgHtml = img ? `<img src="${escHtml(img)}" alt="${escHtml(p.title)}" loading="lazy" width="56" height="56">` : '';
      const price = p.price ? formatPrice(p.price, p.currency || 'AED') : (p.price_min ? formatPrice(p.price_min) : '');
      const badge = p.available === false ? '' : `<span class="aiwa-ss__product-badge">${this.t('Same-day','اليوم')}</span>`;
      return `<a class="aiwa-ss__product" data-aiwa-ss-result data-kind="product" data-handle="${escHtml(p.handle)}"
                 href="${escHtml(p.url)}">
                 <div class="aiwa-ss__product-media">${imgHtml}</div>
                 <div class="aiwa-ss__product-meta">
                   <div class="aiwa-ss__product-title">${escHtml(p.title)}</div>
                   <div class="aiwa-ss__product-row">
                     ${price ? `<span class="aiwa-ss__product-price">${escHtml(price)}</span>` : ''}
                     ${badge}
                   </div>
                 </div>
              </a>`;
    }

    collectionHTML(c) {
      return `<a class="aiwa-ss__collection" data-aiwa-ss-result data-kind="collection" data-handle="${escHtml(c.handle)}"
                 href="${escHtml(c.url)}">
                 <span class="aiwa-ss__collection-icon">
                   <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                     <rect x="3" y="3" width="6" height="6" rx="1"/><rect x="11" y="3" width="6" height="6" rx="1"/>
                     <rect x="3" y="11" width="6" height="6" rx="1"/><rect x="11" y="11" width="6" height="6" rx="1"/>
                   </svg>
                 </span>
                 <span class="aiwa-ss__collection-title">${escHtml(c.title)}</span>
              </a>`;
    }

    articleHTML(a) {
      return `<a class="aiwa-ss__article" data-aiwa-ss-result data-kind="article" href="${escHtml(a.url)}">
                 <span class="aiwa-ss__collection-icon">
                   <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                     <path d="M5 3h10v14H5zM8 7h4M8 11h4"/>
                   </svg>
                 </span>
                 <span class="aiwa-ss__collection-title">${escHtml(a.title)}</span>
              </a>`;
    }

    pageHTML(p) {
      return `<a class="aiwa-ss__page" data-aiwa-ss-result data-kind="page" href="${escHtml(p.url)}">
                 <span class="aiwa-ss__collection-icon">
                   <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                     <path d="M6 3h6l4 4v10H6z"/>
                   </svg>
                 </span>
                 <span class="aiwa-ss__collection-title">${escHtml(p.title)}</span>
              </a>`;
    }

    logClick(el, via) {
      const kind = el.dataset.kind;
      const handle = el.dataset.handle;
      logEvent({
        session_id: this.sessionId,
        event_type: 'click',
        query: this.lastQuery,
        clicked_result: `${kind}:${handle || ''}|via=${via}`,
        ms_to_click: this.lastQueryAt ? nowMs() - this.lastQueryAt : null,
        locale: this.locale
      });
    }

    // --------------------------------------------------------
    // Empty state (chips + recent)
    // --------------------------------------------------------
    renderEmpty() {
      this.emptyEl.hidden = false;
      this.resultsEl.hidden = true;
      this.zeroEl.hidden = true;

      const chips = this.chipsForCurrentContext();

      const recentSearches = readTTLList(STORAGE_KEYS.recentSearches, RECENT_TTL_MS);
      const recentViews    = readTTLList(STORAGE_KEYS.recentViews, RECENT_TTL_MS);

      let html = '';

      // 1. Personalized: "Reorder X" if returning customer with recent order
      if (this.customerLastOrder && this.customerLastOrder.handle) {
        html += `<div class="aiwa-ss__section-title">${this.t('Welcome back','أهلاً بعودتك')}</div>`
              + `<div class="aiwa-ss__chips">
                   <a class="aiwa-ss__chip aiwa-ss__chip--pinned"
                      href="/products/${escHtml(this.customerLastOrder.handle)}"
                      data-aiwa-ss-result data-kind="reorder" data-handle="${escHtml(this.customerLastOrder.handle)}">
                      ${this.t('Reorder','أعد الطلب')}: ${escHtml((this.customerLastOrder.title||'').slice(0,40))}
                   </a>
                 </div>`;
      }

      // 2. ICP chips
      html += `<div class="aiwa-ss__section-title">${this.t('Popular picks','خيارات شائعة')}</div>`
            + `<div class="aiwa-ss__chips">`
            + chips.map(c => this.chipHTML(c, false)).join('')
            + `</div>`;

      // 3. Recent searches
      if (recentSearches.length) {
        html += `<div class="aiwa-ss__section-title">${this.t('Recent searches','البحث الأخير')}</div>`
              + `<div class="aiwa-ss__chips">`
              + recentSearches.map(it =>
                  `<span class="aiwa-ss__chip" data-aiwa-ss-recent="search" data-val="${escHtml(it.v)}">
                     ${escHtml(it.v)}
                     <button type="button" class="aiwa-ss__chip-dismiss" aria-label="Remove" data-aiwa-ss-recent-del="search" data-val="${escHtml(it.v)}">
                       <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.4" fill="none"/></svg>
                     </button>
                   </span>`
                ).join('')
              + `</div>`;
      }

      // 4. Recently viewed products
      if (recentViews.length) {
        const views = recentViews.map(r => {
          try { return JSON.parse(r.v); } catch { return null; }
        }).filter(Boolean);
        if (views.length) {
          html += `<div class="aiwa-ss__section-title">${this.t('Recently viewed','شُوهِدَ مؤخراً')}</div>`
                + `<div class="aiwa-ss__chips">`
                + views.slice(0, 5).map(v =>
                    `<a class="aiwa-ss__chip" href="${escHtml(v.path)}" data-aiwa-ss-result data-kind="recent_view" data-handle="${escHtml(v.handle)}">${escHtml((v.title||v.handle).slice(0, 32))}</a>`
                  ).join('')
                + `</div>`;
        }
      }

      this.emptyEl.innerHTML = html;

      // Wire chip interactions
      $$(this.emptyEl, '[data-aiwa-ss-recent="search"]').forEach(el => {
        el.addEventListener('click', (e) => {
          if (e.target.closest('[data-aiwa-ss-recent-del]')) return;
          const v = el.dataset.val;
          this.input.value = v;
          this.clearBtn.hidden = false;
          this.query(v);
        });
      });
      $$(this.emptyEl, '[data-aiwa-ss-recent-del]').forEach(el => {
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          const v = el.dataset.val;
          removeRecent(STORAGE_KEYS.recentSearches, v);
          this.renderEmpty();
        });
      });
      $$(this.emptyEl, '[data-aiwa-ss-chip]').forEach(el => {
        el.addEventListener('click', () => {
          logEvent({ session_id: this.sessionId, event_type: 'chip_click', query: el.dataset.label, locale: this.locale });
        });
      });
      $$(this.emptyEl, '[data-aiwa-ss-result]').forEach(el => {
        el.addEventListener('click', () => this.logClick(el, 'empty_state'));
      });
    }

    chipHTML(c, pinned) {
      const q = c.q ? `?q=${encodeURIComponent(c.q)}` : '';
      const href = c.handle ? `/collections/${escHtml(c.handle)}${q}` : (c.url || '/');
      const cls = pinned ? 'aiwa-ss__chip aiwa-ss__chip--pinned' : 'aiwa-ss__chip';
      return `<a class="${cls}" href="${href}"
                data-aiwa-ss-chip data-aiwa-ss-result data-kind="chip" data-handle="${escHtml(c.handle||'')}" data-label="${escHtml(c.label)}">
                ${escHtml(c.label)}
              </a>`;
    }

    chipsForCurrentContext() {
      const ctx = getPageContext();
      const icpList = (this.intents.icp_chips && this.intents.icp_chips[this.icp]) || [];
      const defaultList = (this.intents.icp_chips && this.intents.icp_chips.default) || [];
      const tod = sessionStorage.getItem('aiwa_time_bucket') || '';
      const todList = (this.intents.time_of_day && this.intents.time_of_day[tod]) || [];

      // Stitch: page-context first, then ICP, then time-of-day, then default — dedupe by label
      let candidates = [];
      if (ctx.type === 'collection' && ctx.handle) {
        candidates.push({ label: this.t('More in this collection','المزيد من هذه المجموعة'), handle: ctx.handle });
      }
      candidates = candidates.concat(icpList, todList, defaultList);

      const seen = new Set();
      const out = [];
      for (const c of candidates) {
        if (!c || !c.label || seen.has(c.label)) continue;
        seen.add(c.label);
        out.push(c);
        if (out.length === 6) break;
      }
      return out;
    }

    // --------------------------------------------------------
    // Zero-result rescue
    // --------------------------------------------------------
    renderZero(rawQuery) {
      logEvent({
        session_id: this.sessionId,
        event_type: 'zero_result',
        query: rawQuery,
        result_count: 0,
        locale: this.locale
      });
      const chips = (this.intents.icp_chips && this.intents.icp_chips.default) || [];
      const waHref = `https://wa.me/971525866552?text=${encodeURIComponent(this.t(`Hi, I'm looking for: ${rawQuery}`, `مرحباً، أبحث عن: ${rawQuery}`))}`;

      this.emptyEl.hidden = true;
      this.resultsEl.hidden = true;
      this.zeroEl.hidden = false;
      this.zeroEl.innerHTML = `
        <h3 class="aiwa-ss__zero-title">${this.t(`No matches for "${escHtml(rawQuery)}"`, `لا نتائج لـ "${escHtml(rawQuery)}"`)}</h3>
        <p class="aiwa-ss__zero-sub">${this.t('Try one of these — or message us, we handcraft on request.', 'جرب أحد هذه — أو راسلنا، نُعدها يدوياً عند الطلب.')}</p>
        <div class="aiwa-ss__zero-chips">${chips.slice(0, 4).map(c => this.chipHTML(c, true)).join('')}</div>
        <a class="aiwa-ss__zero-cta" href="${waHref}" target="_blank" rel="noopener" data-aiwa-ss-zero-cta>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
            <path d="M12 2a10 10 0 0 0-8.9 14.6L2 22l5.5-1.5A10 10 0 1 0 12 2Zm5.8 14.2c-.3.8-1.5 1.5-2.1 1.6-.5.1-1.2.1-1.9-.1-.4-.1-1-.3-1.7-.6-3-1.3-5-4.4-5.1-4.6-.1-.2-1.2-1.6-1.2-3 0-1.5.8-2.2 1-2.5.3-.3.6-.4.8-.4h.6c.2 0 .5.1.7.5.3.6.9 2.1 1 2.3.1.1.1.3 0 .5 0 .2-.1.3-.2.5-.1.1-.3.4-.4.5-.1.1-.3.3-.1.5.1.3.7 1.2 1.6 2 1.1 1 2 1.3 2.3 1.4.3.1.5.1.6 0 .2-.1.7-.9.9-1.2.2-.3.4-.2.6-.1.3.1 1.7.8 1.9.9.3.1.5.2.5.3.1.1.1.7-.2 1.5z"/>
          </svg>
          ${this.t('Chat with our florist','تحدث مع منسقنا')}
        </a>
      `;
      $(this.zeroEl, '[data-aiwa-ss-zero-cta]').addEventListener('click', () => {
        logEvent({ session_id: this.sessionId, event_type: 'click', clicked_result: 'whatsapp_from_zero', query: rawQuery, locale: this.locale });
      });
    }

    // --------------------------------------------------------
    // i18n shortcut
    // --------------------------------------------------------
    t(en, ar) { return this.locale === 'ar' ? ar : en; }
  }

  customElements.define('aiwa-sentient-search', AiwaSentientSearch);
})();

